import {
  DocumentReference,
  getFirestore,
  setDoc,
  doc,
  collection,
} from 'firebase/firestore'
import {
  getStorage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from 'firebase/storage'
import { useState } from 'react'
import { useToast } from '@chakra-ui/react'

import useHandleError from '../../../../hooks/useHandleError'
import FirestoreUser, {
  DiagnosisDocument,
} from '../../../../types/FirestoreUser'
import { primaryServices } from '../../../../utils/firebaseServiceContext'

interface UseAdminFileUploadParams {
  uid: string
  userRef: DocumentReference<FirestoreUser>
  currentDiagnosisDocumentation: DiagnosisDocument[] | undefined
}

interface UseAdminFileUploadReturn {
  adminFiles: FileList | null
  setAdminFiles: (files: FileList | null) => void
  uploading: boolean
  handleUpload: () => Promise<void>
}

export function useAdminFileUpload({
  uid,
  userRef,
  currentDiagnosisDocumentation,
}: UseAdminFileUploadParams): UseAdminFileUploadReturn {
  const [adminFiles, setAdminFiles] = useState<FileList | null>(null)
  const [uploading, setUploading] = useState(false)
  const handleError = useHandleError()
  const toast = useToast()

  async function appendDiagnosisDocuments(files: FileList) {
    const existing = currentDiagnosisDocumentation || []
    const updated = [...existing]

    for (let i = 0; i < files.length; i++) {
      const file = files.item(i)
      if (!file) continue

      const { id } = doc(collection(primaryServices.firestore, 'a'))
      const extRaw = file.name.split('.').pop()?.toLowerCase()
      const allowed = ['pdf', 'jpg', 'jpeg', 'png']
      const ext = allowed.includes(extRaw || '') ? extRaw : 'pdf'

      const storageReference = storageRef(
        primaryServices.storage,
        `users/${uid}/documents/${id}.${ext}`
      )
      await uploadBytes(storageReference, file)
      const url = await getDownloadURL(storageReference)
      updated.push({ url, uploadedAt: new Date().toISOString() })
    }

    await setDoc(
      userRef,
      { profile: { diagnosisDocumentation: updated } },
      { merge: true }
    )
  }

  async function handleUpload() {
    if (!adminFiles || !adminFiles.length) return
    setUploading(true)
    try {
      await appendDiagnosisDocuments(adminFiles)
      setAdminFiles(null)
      toast({
        status: 'success',
        title: 'Documents uploaded successfully',
        isClosable: true,
      })
    } catch (e) {
      handleError(e)
    } finally {
      setUploading(false)
    }
  }

  return {
    adminFiles,
    setAdminFiles,
    uploading,
    handleUpload,
  }
}
